import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { dataApi, getCustomerOrganisation } from '../services/api';
import appwrite, { client, DATABASE_ID, COLLECTIONS, FUNCTION_IDS } from '../services/appwrite';
import { toast } from 'sonner';
import { ChevronLeft, Link as LinkIcon, BookOpenText, ExternalLink, Upload, Sparkles, UserPlus, CarFront, Loader2, Building2, User, Filter, Plus, TriangleAlert } from 'lucide-react';
import PageHeaderActions from '../components/PageHeaderActions';
import RecordHistoryButton from '../components/history/RecordHistoryButton';

// Realtime collaboration hooks
import { useRealtimeCollaboration } from '../hooks/useRealtimeCollaboration';
import { canUpdate, isDeleted, purgeDate } from '../lib/records';
import { restoreRecord } from '../services/records';
import { useObfuscate } from '../hooks/useObfuscate';
import { useDevQuoteRequest } from '../context/DevModeContext';
import { useLicence } from '../context/LicenceContext';

// shadcn UI components - now with presence indicators
import { Button } from '../components/ui/button';
import { PresenceInput } from '../components/ui/presence-input';
import { PresenceTextarea } from '../components/ui/presence-textarea';
import { PresenceSelect, SelectContent, SelectItem } from '../components/ui/presence-select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import CollaborationIndicator from '../components/ui/collaboration-indicator';

// Existing components we'll keep
import ImageManager from '../components/ImageManager';
import DamageImageForm from './overlays/DamageImageForm';
import LinkCustomerDialog from './overlays/LinkCustomerDialog';
import LinkVehicleDialog from './overlays/LinkVehicleDialog';
import ImageUploadDialog from './overlays/ImageUploadDialog';
import QuoteRequestChecklist from '../components/QuoteRequestChecklist';
import { SectionHint } from '../components/ui/section-hint';
import { Tooltip } from '../components/ui/tooltip';
import DamageHotspotSelector from '../components/DamageHotspotSelector';
import DamagePartsList from '../components/DamagePartsList';
import AssessmentProgress from '../components/AssessmentProgress';
import VehicleSectionPicker from '../components/VehicleSectionPicker';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { sortZones } from '../lib/vehicleZones';
import QuoteEditor from '../components/QuoteEditor';
import { QuoteModelProvider } from '../context/QuoteModelContext';
import CustomerForm from './overlays/CustomerForm';
import VehicleForm from './overlays/VehicleForm';
import OrganizationForm from './overlays/OrganizationForm';
import CustomerOrganisationForm from './overlays/CustomerOrganisationForm';
import CollapseExpandButton from '../components/ui/collapse-expand-button';
import Timeline from '../components/Timeline';
import NoteForm from './overlays/NoteForm';

// Damage assessment view modes. "All" is the per-zone parts list; the others list
// just the R&R / blend / replacement items, grouped by section.
const DAMAGE_VIEW_MODES = [
  { id: 'sections', label: 'All' },
  { id: 'rr', label: 'R&R' },
  { id: 'blend', label: 'Blend' },
  { id: 'parts', label: 'Parts' },
];

// Panel Quote is pushed to at most once every two minutes — back-to-back saves
// of the same job upset its locking.
const PANEL_QUOTE_SYNC_COOLDOWN_MS = 2 * 60 * 1000;

const renderSkeletonLayout = (banner) => (
  <div className="p-6 space-y-6">
    {banner}
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <Skeleton className="h-10 w-64" />
      <div className="flex gap-2">
        <Skeleton className="h-10 w-28" />
        <Skeleton className="h-10 w-28" />
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4">
        {[0, 1, 2].map((key) => (
          <Card key={`skeleton-${key}`}>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="space-y-4">
        {[0, 1].map((key) => (
          <Card key={`sidebar-skeleton-${key}`}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

// Normalise an image record/realtime payload for page state — keeps the full
// document plus the derived fields the viewer and manager need
const mapImageRecord = (img) => ({
  ...img,
  id: img.$id || img.id,
  url: img.url || (img.storagePath ? dataApi.getImageUrl(img.storagePath) : undefined),
  name: img.filename || 'Quote image',
  tags: img.tags || '[]',
  zone: img.zone || ''
});

const sortImagesByOrder = (records) =>
  [...records].sort(
    (a, b) => (a.imageOrder ?? 0) - (b.imageOrder ?? 0)
      || String(a.$createdAt || '').localeCompare(String(b.$createdAt || ''))
  );

// customer_organisations store phones/emails as JSON string arrays — pull the first
const firstOfJsonArray = (val) => {
  if (!val) return '';
  if (Array.isArray(val)) return val[0] || '';
  try {
    const arr = JSON.parse(val);
    return Array.isArray(arr) ? (arr[0] || '') : (val || '');
  } catch {
    return val || '';
  }
};

const QuoteRequestDetailsRealtime = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);
  
  // Initialize realtime collaboration for quote request
  const {
    data: realtimeData,
    isOnline,
    activeUsers,
    currentUser,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = useRealtimeCollaboration('quote_requests', id, {});

  // Dev-mode PII obfuscation (no-op unless dev mode is active)
  const priv = useObfuscate();

  // Offer this quote request to the dev menu, which hosts the destructive
  // "reset ML & re-detect" testing action (the user-facing re-run in the Damage
  // section deliberately leaves ML results, and therefore angle detection, alone).
  useDevQuoteRequest(id, quote?.organisationId || realtimeData?.organisationId);

  // Overlay form states
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [vehicleFormOpen, setVehicleFormOpen] = useState(false);
  const [organizationFormOpen, setOrganizationFormOpen] = useState(false);
  const [customerOrganisationFormOpen, setCustomerOrganisationFormOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [organisationFormInitialName, setOrganisationFormInitialName] = useState('');
  
  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [quoteRequestImages, setQuoteRequestImages] = useState([]);
  const [damageZones, setDamageZones] = useState([]);
  const [damageFilterZone, setDamageFilterZone] = useState(null);
  const [checkZoneHasPartsRef, setCheckZoneHasPartsRef] = useState(null);
  // Damage assessment toolbar: which view, whether the filter pane is expanded
  // (the Filter button is available in every view), and the Add Section popover.
  const [damageViewMode, setDamageViewMode] = useState('sections');
  const [damageFilterOpen, setDamageFilterOpen] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  
  // Data for dropdowns
  const [insurers, setInsurers] = useState([]); // [{ id, name, configId }]
  const [suggestedHourlyRate, setSuggestedHourlyRate] = useState(null);
  const [hasInsurerGuidelines, setHasInsurerGuidelines] = useState(false);
  // Some insurers (e.g. IAG) do not allow quote line item dollar values to be edited.
  const [canUpdateLinePrice, setCanUpdateLinePrice] = useState(true);
  
  // Note state
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [timelineKey, setTimelineKey] = useState(0);
  const [isHoveringTimeline, setIsHoveringTimeline] = useState(false);


  // AI damage assessment trigger state
  const [generatingAssessment, setGeneratingAssessment] = useState(false);
  // Plan entitlements, used only to decide what the assessment button offers.
  // The orchestrator enforces the limit itself — this just means an over-limit
  // shop gets an explanation instead of a run that silently refuses.
  const { checkAiAssessment, refresh: refreshLicence } = useLicence();
  // Whether any section holds damage parts (reported by DamagePartsList).
  // null until the damage model has loaded, so the empty state isn't flashed.
  // Once there is an assessment to protect, the AI updates it rather than
  // replacing it — see handleGenerateAssessment.
  //
  // A boolean rather than the list itself: the list is only ever read as
  // "is there anything here", and holding it in state re-rendered this whole
  // page every time the list changed — which, mid-drag, is every time a part
  // crosses into another section.
  const [hasDamageParts, setHasDamageParts] = useState(null);

  // Link customer/vehicle dialogs + photo upload dialog
  const [linkCustomerOpen, setLinkCustomerOpen] = useState(false);
  const [linkVehicleOpen, setLinkVehicleOpen] = useState(false);
  const [linkDriverOpen, setLinkDriverOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Files dropped on the page, handed to the upload dialog to stage, and whether
  // files are being dragged over the page right now (shows the drop overlay).
  const [droppedFiles, setDroppedFiles] = useState(null);
  const [fileDragActive, setFileDragActive] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  // Full business record for the linked customerOrganisation. Kept separately
  // because realtime echoes carry the relationship as a bare ID, not the object.
  const [linkedOrganisation, setLinkedOrganisation] = useState(null);


  // Panel Quote update state
  const [updatingPanelQuote, setUpdatingPanelQuote] = useState(false);
  const [optimisticLastSync, setOptimisticLastSync] = useState(null);
  const [showPanelQuoteConfirm, setShowPanelQuoteConfirm] = useState(false);
  // A request has exactly one quote, so there is nothing to choose between —
  // both Panel Quote dialogs just confirm the one quote the request holds.
  const pushStartedFromRef = useRef(null);
  // Panel Quote handoff (creating the job, as opposed to updating an existing one)
  const [creatingPanelQuote, setCreatingPanelQuote] = useState(false);
  const [showPanelQuoteCreate, setShowPanelQuoteCreate] = useState(false);
  const [panelQuoteInsurerMapping, setPanelQuoteInsurerMapping] = useState(undefined);

  // Card collapse/expand state
  const [cardStates, setCardStates] = useState({
    insurer: true,
    damage: true,
    quotes: true,
    editQuote: true,
    images: true,
    timeline: true,
    vehicle: true,
    customer: true,
    panelQuote: true
  });

  // Load dropdown data from API
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const insurersData = await dataApi.insurerList();
        const insurersList = (insurersData || [])
          .filter(item => item?.$id && item?.name)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(item => ({ id: item.$id, name: item.name, configId: item.insurerConfigId }));
        setInsurers(insurersList);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };

    loadDropdownData();
  }, []);

  // Fetch initial quote request details
  const fetchQuoteRequestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dataApi.quoteRequestGet(id);
      
      if (!data) {
        setError('Quote request not found');
        return;
      }

      setQuote(data);
      
      // Update realtime collaboration initial data
      // Note: the hook will handle syncing from here
    } catch (err) {
      console.error('Error fetching quote request:', err);
      setError('Failed to load quote request details');
      toast.error('Failed to load quote request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuoteRequestDetails();
  }, [fetchQuoteRequestDetails]);

  // Load quote request images (full records — the manager needs category/order)
  useEffect(() => {
    const loadImages = async () => {
      if (!id) return;

      try {
        const imagesData = await dataApi.quoteRequestImages(id);
        setQuoteRequestImages(sortImagesByOrder(imagesData.map(mapImageRecord)));
      } catch (err) {
        console.error('Error loading images:', err);
      }
    };

    loadImages();
  }, [id]);

  // Subscribe to quote request images for realtime updates (create/update/delete)
  useEffect(() => {
    if (!id) return;

    const channel = `databases.${DATABASE_ID}.collections.${COLLECTIONS.QUOTE_REQUEST_IMAGES}.documents`;

    const unsubscribe = client.subscribe(channel, (response) => {
      if (!response.events.some(event => event.includes('documents'))) return;

      const payload = response.payload;
      if (payload?.quoteRequestId !== id) return;

      const imageId = payload.$id || payload.id;
      // Deleting a photo is soft: it arrives as an update carrying deletedAt,
      // and a restore as an update clearing it.
      const isDelete = response.events.some(e => e.includes('delete')) || Boolean(payload.deletedAt);
      const isRestore = !isDelete && response.events.some(e => e.includes('update'));

      setQuoteRequestImages(prev => {
        if (isDelete) {
          return prev.filter(img => img.id !== imageId);
        }

        const existingIndex = prev.findIndex(img => img.id === imageId);
        if (existingIndex >= 0) {
          // Merge the full payload; keep the derived url (payloads don't carry one)
          const updated = [...prev];
          updated[existingIndex] = mapImageRecord({
            ...updated[existingIndex],
            ...payload,
            url: updated[existingIndex].url
          });
          return sortImagesByOrder(updated);
        }
        // A photo this screen does not have yet: newly uploaded, or restored.
        if (isRestore || response.events.some(e => e.includes('create'))) {
          return sortImagesByOrder([...prev, mapImageRecord(payload)]);
        }
        return prev;
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [id]);

  // Subscribe to customer changes (independent of vehicle — blank requests link them separately)
  useEffect(() => {
    if (!quote?.customerId) return;

    const unsubscribeCustomer = appwrite.subscribeToDocument(
      'customers',
      quote.customerId,
      (response) => {
        if (response.payload) {
          setQuote(prev => ({
            ...prev,
            customer: response.payload
          }));
        }
      }
    );

    return () => {
      if (typeof unsubscribeCustomer === 'function') unsubscribeCustomer();
    };
  }, [quote?.customerId]);

  // Subscribe to vehicle changes
  useEffect(() => {
    if (!quote?.customerVehicleId) return;

    const unsubscribeVehicle = appwrite.subscribeToDocument(
      'customer_vehicles',
      quote.customerVehicleId,
      (response) => {
        if (response.payload) {
          setQuote(prev => ({
            ...prev,
            vehicle: response.payload
          }));
        }
      }
    );

    return () => {
      if (typeof unsubscribeVehicle === 'function') unsubscribeVehicle();
    };
  }, [quote?.customerVehicleId]);

  // Merge quote data with realtime data for display
  const displayData = {
    ...quote,
    ...realtimeData
  };

  // ── Panel Quote sync cooldown ──────────────────────────────────────────────
  // Pushing to Panel Quote is rate-limited to one sync every two minutes. The
  // countdown is derived from the clock, so without a tick of its own it would
  // only be recomputed when something else re-rendered the page — leaving the
  // button disabled, and the assessor reloading the page to sync again.
  // The optimistic stamp is taken when the push starts, the stored one when it
  // finishes, so the later of the two is the sync the cooldown runs from.
  const panelQuoteLastSyncMs = [optimisticLastSync, displayData.panelQuoteLastSync]
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((value) => Number.isFinite(value))
    .reduce((latest, value) => (latest == null || value > latest ? value : latest), null);
  // Panel Quote bills an insurance job to the insurer, so an unmapped insurer
  // means the handoff cannot run. Check it here rather than letting a background
  // run fail out of sight.
  useEffect(() => {
    if (displayData.panelQuoteJobId || !displayData.isInsuranceWork) {
      setPanelQuoteInsurerMapping(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mappings = await dataApi.panelQuoteInsurerMappings();
        if (cancelled) return;
        const insurerId = displayData.insuranceCompany || displayData.vehicleInsurerId;
        setPanelQuoteInsurerMapping(mappings.find((m) => m.insurerId === insurerId) || null);
      } catch (err) {
        // Not being able to check is not the same as not being mapped — leave
        // the button enabled and let the server have the final word.
        console.warn('Could not load Panel Quote insurer mappings:', err.message);
        if (!cancelled) setPanelQuoteInsurerMapping(undefined);
      }
    })();
    return () => { cancelled = true; };
  }, [displayData.panelQuoteJobId, displayData.isInsuranceWork, displayData.insuranceCompany, displayData.vehicleInsurerId]);

  // Once the background run links the job, the "create" card has done its job.
  useEffect(() => {
    if (displayData.panelQuoteJobId) setCreatingPanelQuote(false);
  }, [displayData.panelQuoteJobId]);

  // A push is finished when the record comes back with a newer sync stamp. Until
  // then the button stays locked: a second push while the first still holds
  // Panel Quote's job lock is exactly what makes it fail. The timeout is the
  // escape hatch for a run that dies without ever writing one.
  useEffect(() => {
    if (!updatingPanelQuote) return undefined;
    // Both sides normalised: an unsynced request reads `undefined` here but was
    // stored as `null`, and comparing those directly would report the push
    // finished the instant it started.
    if ((displayData.panelQuoteLastSync || null) !== pushStartedFromRef.current) {
      pushStartedFromRef.current = null;
      setUpdatingPanelQuote(false);
      toast.success('Quote sent to Panel Quote');
      return undefined;
    }
    const timer = setTimeout(() => {
      pushStartedFromRef.current = null;
      setUpdatingPanelQuote(false);
      toast.error('Panel Quote did not confirm the update. Check the job before sending again.');
    }, 3 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [updatingPanelQuote, displayData.panelQuoteLastSync]);

  const [panelQuoteNow, setPanelQuoteNow] = useState(() => Date.now());
  useEffect(() => {
    if (!displayData.panelQuoteJobId || panelQuoteLastSyncMs == null) return undefined;
    // Once a second while the cooldown runs, so the button re-enables the moment
    // it expires; once a minute afterwards, which is all "3m ago" needs. Each
    // tick reschedules the next one, so the rate follows the state.
    const coolingDown = panelQuoteNow - panelQuoteLastSyncMs < PANEL_QUOTE_SYNC_COOLDOWN_MS;
    const timer = setTimeout(() => setPanelQuoteNow(Date.now()), coolingDown ? 1000 : 60000);
    return () => clearTimeout(timer);
  }, [displayData.panelQuoteJobId, panelQuoteLastSyncMs, panelQuoteNow]);

  // A quote request is linked to EITHER an individual customer OR a business.
  // customerOrganisation can arrive as a populated object (initial load) or a
  // bare ID (realtime echo) — normalise to its id, then keep the full record in
  // linkedOrganisation state.
  const currentOrgId = (() => {
    const co = displayData.customerOrganisation;
    if (!co) return null;
    return typeof co === 'object' ? co.$id : co;
  })();

  useEffect(() => {
    if (!currentOrgId) { setLinkedOrganisation(null); return undefined; }
    if (linkedOrganisation?.$id === currentOrgId) return undefined;
    const co = displayData.customerOrganisation;
    if (co && typeof co === 'object' && co.$id === currentOrgId) {
      setLinkedOrganisation(co);
      return undefined;
    }
    let cancelled = false;
    getCustomerOrganisation(currentOrgId)
      .then((full) => { if (!cancelled) setLinkedOrganisation(full); })
      .catch((err) => console.error('Failed to load linked business:', err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  // When a customer/vehicle gets linked (by this user or a teammate — realtime
  // payloads only carry the ids), fetch the enriched record and re-arm the
  // document subscriptions above
  useEffect(() => {
    const linkedCustomerId = realtimeData?.customerId ?? quote?.customerId;
    if (!linkedCustomerId) return undefined;
    if (quote?.customerId === linkedCustomerId && quote?.customer?.$id === linkedCustomerId) return undefined;

    let cancelled = false;
    dataApi.customerGet(linkedCustomerId)
      .then((customer) => {
        if (!cancelled) {
          setQuote(prev => ({ ...prev, customerId: linkedCustomerId, customer }));
        }
      })
      .catch((err) => console.error('Failed to load linked customer:', err));

    return () => { cancelled = true; };
  }, [realtimeData?.customerId, quote?.customerId, quote?.customer?.$id]);

  useEffect(() => {
    const linkedVehicleId = realtimeData?.customerVehicleId ?? quote?.customerVehicleId;
    if (!linkedVehicleId) return undefined;
    if (quote?.customerVehicleId === linkedVehicleId && quote?.vehicle?.$id === linkedVehicleId) return undefined;

    let cancelled = false;
    dataApi.vehicleGet(linkedVehicleId)
      .then((vehicle) => {
        if (!cancelled) {
          setQuote(prev => ({ ...prev, customerVehicleId: linkedVehicleId, vehicle }));
        }
      })
      .catch((err) => console.error('Failed to load linked vehicle:', err));

    return () => { cancelled = true; };
  }, [realtimeData?.customerVehicleId, quote?.customerVehicleId, quote?.vehicle?.$id]);

  useEffect(() => {
    if (Array.isArray(displayData?.damageZones)) {
      setDamageZones(displayData.damageZones);
    }
  }, [displayData?.damageZones]);

  const handleBackNavigation = () => {
    const from = localStorage.getItem('quoteRequestDetailsReturnPath');
    
    if (from) {
      localStorage.removeItem('quoteRequestDetailsReturnPath');
      navigate(from);
    } else {
      navigate('/quote-requests');
    }
  };

  // Field change handlers with realtime sync
  const handleFieldChange = (field, value) => {
    updateField(field, value);
  };

  const handleFieldFocus = (fieldName) => {
    trackFieldEdit(fieldName);
  };

  const handleFieldBlur = () => {
    clearFieldEdit();
  };

  const handleInsurerChange = (value) => {
    updateField('insuranceCompany', value === 'other' ? null : value);
    if (value !== 'other') {
      updateField('otherInsurer', null);
    }
  };

  const handleOpenInsurerGuidelines = () => {
    if (!selectedInsurerConfigId) {
      toast.warning('No insurer guidelines available for this request');
      return;
    }
    const url = `${window.location.origin}/insurer-guidelines/${selectedInsurerConfigId}`;
    window.open(url, '_blank', 'noopener');
  };

  const insurerMap = useMemo(() => {
    const map = new Map();
    insurers.forEach(ins => map.set(ins.id, ins.name));
    return map;
  }, [insurers]);

  const selectedInsurerValue = useMemo(() => {
    const current = displayData.insuranceCompany;
    if (current && insurerMap.has(current)) return current;
    const byName = current ? insurers.find(ins => ins.name === current) : null;
    if (byName) return byName.id;
    if (!current && displayData.otherInsurer) return 'other';
    if (current === 'Other') return 'other';
    return current || undefined;
  }, [displayData.insuranceCompany, displayData.otherInsurer, insurerMap, insurers]);

  const selectedInsurerConfigId = useMemo(() => {
    if (!displayData.isInsuranceWork) return null;
    const selected = insurers.find(ins => ins.id === selectedInsurerValue);
    return selected?.configId || null;
  }, [displayData.isInsuranceWork, insurers, selectedInsurerValue]);

  const showOtherInsurer = selectedInsurerValue === 'other' || (!selectedInsurerValue && displayData.otherInsurer);

  useEffect(() => {
    let cancelled = false;

    const preloadInsurerConfig = async () => {
      if (!selectedInsurerConfigId) {
        setSuggestedHourlyRate(null);
        setHasInsurerGuidelines(false);
        setCanUpdateLinePrice(true);
        return;
      }
      try {
        let config = await dataApi.insurerConfigGet(selectedInsurerConfigId);
        if (cancelled) return;

        let rate = config?.hourlyRate ?? null;
        let hasGuidelines = !!config?.repairGuidelinesFileId;
        let canUpdatePrice = config?.canUpdateQuoteLineItemPrice ?? null;

        // If child config is missing data, check parent
        if (config?.parentId && (!rate || !hasGuidelines || canUpdatePrice === null)) {
          const parentConfig = await dataApi.insurerConfigGet(config.parentId);
          if (cancelled) return;
          if (!rate && parentConfig?.hourlyRate) {
            rate = parentConfig.hourlyRate;
          }
          if (!hasGuidelines && parentConfig?.repairGuidelinesFileId) {
            hasGuidelines = true;
          }
          if (canUpdatePrice === null) {
            canUpdatePrice = parentConfig?.canUpdateQuoteLineItemPrice ?? null;
          }
        }

        setSuggestedHourlyRate(rate);
        setHasInsurerGuidelines(hasGuidelines);
        setCanUpdateLinePrice(canUpdatePrice ?? true);
        if (!displayData.hourlyRate && rate) {
          updateField('hourlyRate', rate);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Could not load insurer config', err);
          setHasInsurerGuidelines(false);
          setCanUpdateLinePrice(true);
        }
      }
    };

    preloadInsurerConfig();

    return () => {
      cancelled = true;
    };
  }, [displayData.hourlyRate, selectedInsurerConfigId, updateField]);

  const hourlyRateValue = displayData.hourlyRate ?? suggestedHourlyRate ?? '';

  // Derive vehicle type from vehicle classification flags
  const vehicleType = useMemo(() => {
    const v = displayData.vehicle;
    if (!v) return 'standard';
    if (v.isLuxury) return 'luxury';
    if (v.isEuropean) return 'european';
    if (v.isCommercial) return 'commercial';
    return 'standard';
  }, [displayData.vehicle]);

  // Vehicle size drives paint units for insurers sized off the vehicle rather than
  // the panel (Toyota). Unknown vehicles price at the middle band.
  const vehicleSize = useMemo(
    () => displayData.vehicle?.vehicleSize || 'medium',
    [displayData.vehicle]
  );

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Overlay form handlers
  const handleOpenCustomerForm = (customerId) => {
    setSelectedCustomerId(customerId);
    setCustomerFormOpen(true);
  };

  const handleOpenVehicleForm = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setVehicleFormOpen(true);
  };

  const handleCloseCustomerForm = () => {
    setCustomerFormOpen(false);
    setSelectedCustomerId(null);
  };

  const handleCloseVehicleForm = () => {
    setVehicleFormOpen(false);
    setSelectedVehicleId(null);
  };

  const handleCloseOrganizationForm = () => {
    setOrganizationFormOpen(false);
  };

  const handleCustomerOrganisationFormSave = (newOrganisation) => {
    handleCloseCustomerOrganisationForm();
    toast.success('Organization created successfully');
  };

  const handleCloseCustomerOrganisationForm = () => {
    setCustomerOrganisationFormOpen(false);
    setOrganisationFormInitialName('');
    setSelectedBusinessId(null);
  };

  const handleOrganizationFormSave = () => {
    handleCloseOrganizationForm();
    toast.success('Organization updated successfully');
  };

  const removeZone = (zoneId) => {
    setDamageZones(prev => prev.filter(id => id !== zoneId));
  };

  // Add an empty section from the "Add Section" picker (kept in walk-around order).
  const addSection = (zoneId) => {
    setDamageZones(prev => (prev.includes(zoneId) ? prev : sortZones([...prev, zoneId])));
    setAddSectionOpen(false);
  };

  // Toggle the filter pane; hiding it clears the active filter so no view is left
  // silently filtered with no visible control to clear it.
  const toggleDamageFilter = () => {
    setDamageFilterOpen(open => {
      if (open) setDamageFilterZone(null);
      return !open;
    });
  };

  // Auto-select zones that have damage parts when data loads
  const handleZonesWithPartsChanged = React.useCallback((zones) => {
    setHasDamageParts(zones.length > 0);
    setDamageZones(prev => {
      // Only add zones that aren't already selected
      const newZones = zones.filter(zone => !prev.includes(zone));
      if (newZones.length > 0) {
        // Keep sections in canonical walk-around order (front→back, top→bottom, left→right)
        return sortZones([...prev, ...newZones]);
      }
      return prev;
    });
  }, []);

  const toggleCard = (cardName) => {
    setCardStates(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Image viewer handlers. `scopedImages` is the list the photo was opened
  // from — a section's photos, or the Photos grid as filtered — and is what
  // next/previous then cycle through. Without one, every photo on the request.
  const handleImageClick = (imageId, scopedImages) => {
    const list = scopedImages?.length ? scopedImages : quoteRequestImages;
    const index = list.findIndex(img => (img.id || img.$id) === imageId);
    if (index >= 0) {
      setImages(list);
      setSelectedImageIndex(index);
      setImageViewerOpen(true);
    }
  };

  // Files dragged in from the desktop can be dropped anywhere on the page: the
  // Add photos dialog opens with them staged under Other. A drop the dialog's
  // own areas took is marked handled (defaultPrevented) and left alone; one
  // landing elsewhere while the dialog is open is added to it.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handlePageDragEnter = (e) => {
    if (isFileDrag(e) && !uploadOpen) setFileDragActive(true);
  };

  const handlePageDragOver = (e) => {
    // Without this the browser opens a dropped file in place of the app.
    if (isFileDrag(e) && !e.defaultPrevented) e.preventDefault();
  };

  const handlePageDrop = (e) => {
    setFileDragActive(false);
    if (e.defaultPrevented || !e.dataTransfer?.files?.length) return;
    e.preventDefault();
    setDroppedFiles(Array.from(e.dataTransfer.files));
    setUploadOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setDroppedFiles(null);
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
  };

  const handleImageUpdate = (imageId, updateData) => {
    // Update local images state with new data
    setImages(prev => prev.map(img => 
      img.id === imageId 
        ? { ...img, ...updateData }
        : img
    ));
  };

  // Note handlers
  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setNoteFormOpen(true);
  };

  const handleNewNote = () => {
    setSelectedNote(null);
    setNoteFormOpen(true);
  };

  const handleNoteClose = () => {
    setNoteFormOpen(false);
    setSelectedNote(null);
  };

  const handleNoteSave = () => {
    // Refresh timeline
    setTimelineKey(prev => prev + 1);
  };

  const handleNoteDelete = () => {
    // Refresh timeline
    setTimelineKey(prev => prev + 1);
  };


  // Link a customer (individual) or business (organisation) to this quote request.
  // A quote request is for one OR the other — linking one clears the other.
  const handleCustomerLinked = async (entity, type = 'individual') => {
    setLinkCustomerOpen(false);
    try {
      if (type === 'organisation') {
        await dataApi.quoteRequestLinkOrganisation(id, entity.$id);
        setQuote(prev => ({
          ...prev,
          customerId: '',
          customer: null,
          customerOrganisation: entity
        }));
        toast.success('Business linked');

        // A business owns the vehicle — reflect that on the linked vehicle
        const vehicleId = displayData.customerVehicleId;
        if (vehicleId && !displayData.vehicle?.ownerOrganisation) {
          try {
            await dataApi.vehicleUpdate(vehicleId, { ownerOrganisation: entity.$id });
            setQuote(prev => ({
              ...prev,
              vehicle: prev.vehicle ? { ...prev.vehicle, ownerOrganisation: entity } : prev.vehicle
            }));
          } catch (ownerErr) {
            console.warn('Could not set the vehicle owner organisation:', ownerErr.message);
          }
        }
        return;
      }

      await dataApi.quoteRequestLinkCustomer(id, entity.$id);
      setQuote(prev => ({ ...prev, customerId: entity.$id, customer: entity, customerOrganisation: null }));
      toast.success('Customer linked');

      // If a vehicle was captured before the customer (vehicle-first flow), give
      // it an owner now. Best-effort — the quote already links the vehicle directly.
      const vehicle = quote?.vehicle || realtimeData?.vehicle;
      const vehicleId = displayData.customerVehicleId;
      if (vehicleId && vehicle && !vehicle.customerId) {
        try {
          await dataApi.vehicleUpdate(vehicleId, { customerId: entity.$id });
          setQuote(prev => ({
            ...prev,
            vehicle: prev.vehicle ? { ...prev.vehicle, customerId: entity.$id } : prev.vehicle
          }));
        } catch (backfillErr) {
          console.warn('Could not associate the linked vehicle with the customer:', backfillErr.message);
        }
      }
    } catch (err) {
      console.error('Failed to link:', err);
      toast.error(err.message || 'Failed to link');
    }
  };

  // Set/replace the driver (a person) on the linked vehicle — contact for a
  // business-owned vehicle. Stored as customer_vehicles.driver relationship.
  const handleDriverLinked = async (driver) => {
    setLinkDriverOpen(false);
    const vehicleId = displayData.customerVehicleId;
    if (!vehicleId) return;
    try {
      await dataApi.vehicleUpdate(vehicleId, { driver: driver.$id });
      setQuote(prev => ({
        ...prev,
        vehicle: prev.vehicle ? { ...prev.vehicle, driver } : prev.vehicle
      }));
      toast.success('Driver linked');
    } catch (err) {
      console.error('Failed to link driver:', err);
      toast.error(err.message || 'Failed to link driver (vehicle writes are blocked until the adas fix)');
    }
  };

  const handleVehicleLinked = async (vehicle) => {
    setLinkVehicleOpen(false);
    try {
      await dataApi.quoteRequestUpdate(id, { customerVehicleId: vehicle.$id });
      setQuote(prev => ({ ...prev, customerVehicleId: vehicle.$id, vehicle }));
      toast.success('Vehicle linked');
    } catch (err) {
      console.error('Failed to link vehicle:', err);
      toast.error(err.message || 'Failed to link vehicle');
    }
  };

  /**
   * Run the AI damage assessment stage. ML detection already ran quietly as the
   * photos uploaded, and is deliberately left alone here — re-running the
   * assessment must not undo the angle model's left/right calls on the photos.
   * (Wiping ML entirely is a dev-menu action.)
   *
   * `merge` is set once there is an assessment to protect: the AI then adds what
   * it found on top of the existing lines instead of replacing them, so a quote
   * that has been reviewed or negotiated survives an update.
   */
  const handleGenerateAssessment = async ({ merge = false } = {}) => {
    // The description is the scope of the claim. Without it the AI has nothing
    // telling it which of the damage on a car belongs to this job, and it will
    // quote the old dent in the other door. The buttons that call this are
    // already disabled without one; this is the choke point every caller goes
    // through, and a run costs a credit either way.
    if (!(displayData.damageDescription || '').trim()) {
      toast.error('Describe the damage first', {
        description: 'The AI reads the description as the scope of the claim. '
          + "Without it there is nothing to tell this job's damage from older damage on the same car.",
        duration: 8000,
        action: {
          label: 'Add one',
          onClick: () => {
            setCardStates((prev) => ({ ...prev, damage: true }));
            setTimeout(() => document.getElementById('damage-description')?.focus(), 0);
          },
        },
      });
      return;
    }

    // Every run costs a credit, including "Update with AI" over an existing
    // assessment — it runs the models again either way.
    const entitlement = checkAiAssessment();
    if (!entitlement.allowed) {
      toast.error(
        `You've used all ${entitlement.limit} AI assessments included this month. Manual quoting still works as normal.`,
        {
          duration: 8000,
          action: { label: 'See plans', onClick: () => navigate('/settings/general') },
        }
      );
      return;
    }

    try {
      setGeneratingAssessment(true);
      await dataApi.generateDamageAssessment(id, { restart: true, merge });
      toast.success(merge
        ? 'Updating with AI — your lines are kept, anything missed will be added'
        : 'AI damage assessment started — parts will appear as they are found');
      // The run consumed a credit; keep the sidebar meter honest.
      refreshLicence();
    } catch (err) {
      console.error('Failed to start damage assessment:', err);
      toast.error('Failed to start assessment: ' + err.message);
      setGeneratingAssessment(false);
    }
  };

  // Reset the local trigger flag once the server reports a state change
  useEffect(() => {
    if (generatingAssessment && displayData.damageAssessmentStatus === 'processing') {
      setGeneratingAssessment(false);
    }
  }, [generatingAssessment, displayData.damageAssessmentStatus]);

  /**
   * Escape hatch for a run that died mid-flight. A bugged assessment must never
   * leave the record stuck on "processing" with no way forward, so the user can
   * clear the state themselves and try again. Nothing already written is touched.
   */
  const handleStopWaitingForAssessment = async () => {
    try {
      setGeneratingAssessment(false);
      await dataApi.quoteRequestUpdate(id, { damageAssessmentStatus: 'failed' });
      toast.success('Stopped waiting — you can run the assessment again');
    } catch (err) {
      console.error('Failed to clear assessment state:', err);
      toast.error('Could not clear the assessment state: ' + err.message);
    }
  };

  const handleUpdatePanelQuote = () => setShowPanelQuoteConfirm(true);

  /**
   * Push this request's quote to its Panel Quote job, replacing what is there.
   *
   * The push runs in the background, so the button stays locked until the record
   * comes back with a newer `panelQuoteLastSync` — sending twice while the first
   * push still holds Panel Quote's job lock is what makes it fail.
   */
  const handleConfirmPanelQuoteUpdate = async () => {
    setShowPanelQuoteConfirm(false);
    try {
      // Remembered before the optimistic stamp so the effect below can tell a
      // real completion from our own guess at one.
      pushStartedFromRef.current = displayData.panelQuoteLastSync || null;
      setUpdatingPanelQuote(true);
      setOptimisticLastSync(new Date().toISOString());
      const organisationId = quote?.organisationId || realtimeData?.organisationId;

      // No quoteId: a request has one quote and the function resolves it.
      await appwrite.executeFunctionAsync(
        FUNCTION_IDS.PANEL_QUOTE_UPDATE_QUOTE,
        JSON.stringify({
          quoteRequestId: id,
          teamId: organisationId
        })
      );

      toast.info('Sending the quote to Panel Quote…');
    } catch (err) {
      console.error('Failed to update Panel Quote:', err);
      toast.error('Failed to update Panel Quote: ' + err.message);
      pushStartedFromRef.current = null;
      setUpdatingPanelQuote(false);
    }
  };

  /**
   * Hand this request to Panel Quote as a new job.
   *
   * The push itself runs in the background — a wizard, a 4MB form and a save is
   * well past a synchronous execution — so success arrives as panelQuoteJobId
   * appearing on the record via realtime, which swaps this card for the linked
   * one below.
   */
  const handleCreatePanelQuoteJob = async () => {
    setShowPanelQuoteCreate(false);
    try {
      setCreatingPanelQuote(true);
      await dataApi.panelQuoteCreateJob(id);
      toast.success('Creating the job in Panel Quote — it will appear here when it is ready');
    } catch (err) {
      console.error('Failed to create the Panel Quote job:', err);
      toast.error('Could not start the Panel Quote handoff: ' + err.message);
      setCreatingPanelQuote(false);
    }
  };

  const handleOpenPanelQuoteCreate = () => setShowPanelQuoteCreate(true);

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'New', className: 'bg-blue-100 text-blue-800' },
      contacted: { label: 'Contacted', className: 'bg-yellow-100 text-yellow-800' },
      quoted: { label: 'Quoted', className: 'bg-purple-100 text-purple-800' },
      accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800' },
      declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
      completed: { label: 'Completed', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return renderSkeletonLayout();
  }

  if (error && !quote) {
    return (
      <div className="p-8 text-center">
        <div className="p-4 rounded-md mb-4 bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
        <Button variant="outline" onClick={handleBackNavigation}>
          Back to Quote Requests
        </Button>
      </div>
    );
  }

  // Deleted — reached by a stale link or the back button, or deleted by someone
  // else while it was open (the realtime payload carries deletedAt). Deleting is
  // soft, so instead of an editor for a record that is gone from every list,
  // say what happened and offer to bring it back.
  if (isDeleted(displayData)) {
    const mayRestore = canUpdate(displayData, currentUser);
    const formatDay = (value) => new Date(value).toLocaleDateString('en-NZ', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
    const handleRestore = async () => {
      setRestoring(true);
      try {
        const restored = await restoreRecord('quote_requests', displayData);
        setQuote((prev) => ({ ...prev, ...restored, deletedAt: null }));
        toast.success('Quote request restored');
      } catch (err) {
        console.error('Failed to restore quote request:', err);
        toast.error(err?.message || 'Could not restore this quote request');
      } finally {
        setRestoring(false);
      }
    };

    return (
      <div className="p-8 max-w-xl mx-auto text-center space-y-4">
        <h1 className="text-2xl font-semibold">This quote request was deleted</h1>
        <p className="text-muted-foreground">
          Deleted on {formatDay(displayData.deletedAt)}. It will be removed for good on{' '}
          {formatDay(purgeDate(displayData))}{mayRestore ? ' unless it is restored.' : '.'}
        </p>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={handleBackNavigation}>
            Back to Quote Requests
          </Button>
          {mayRestore && (
            <Button onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Restoring…' : 'Restore'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Open the Damage card if it is collapsed, then put the cursor in the
  // description. The checklist and the section bell both promise to take you to
  // the thing they are asking for, and a field inside a folded card does not
  // keep that promise.
  const focusDamageDescription = () => {
    setCardStates((prev) => ({ ...prev, damage: true }));
    // After the card has rendered, or there is nothing to focus yet.
    setTimeout(() => {
      const field = document.getElementById('damage-description');
      if (!field) return;
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus();
    }, 0);
  };

  // The essentials still needed before quoting. Drives the header notification
  // bell and the per-section hint bells — they simply disappear once filled.
  const hasCustomer = !!(displayData.customerId || currentOrgId);
  const hasVehicle = !!displayData.customerVehicleId;
  const hasDamagePhotos = quoteRequestImages.some(img => img.imageCategory === 'damage');
  // What the customer said was wrong. Photo albums do not state scope — a job
  // whose whole quote was one wing-mirror cap arrived with sixteen photos of an
  // otherwise undamaged car — so without this the AI has nothing telling it
  // where to stop, and bills the panel with the old dent in it. The customer
  // intake form has always required it; a request raised in here could reach the
  // AI without one.
  const hasDamageDescription = !!(displayData.damageDescription || '').trim();

  const checklistItems = [
    { key: 'customer', label: 'Add customer', done: hasCustomer, action: () => setLinkCustomerOpen(true) },
    { key: 'vehicle', label: 'Add vehicle', done: hasVehicle, action: () => setLinkVehicleOpen(true) },
    { key: 'description', label: 'Describe the damage', done: hasDamageDescription, action: () => focusDamageDescription() },
    { key: 'photos', label: 'Add photos', done: hasDamagePhotos, action: () => setUploadOpen(true) }
  ];

  // What the AI needs before it can be asked. Photos are what it looks at; the
  // description is what tells it which of the damage in them is this claim.
  const canAssess = hasDamagePhotos && hasDamageDescription;
  const assessBlockedReason = !hasDamagePhotos && !hasDamageDescription
    ? 'Add photos and describe the damage first'
    : !hasDamagePhotos
      ? 'Add photos first, then generate the AI assessment'
      : 'Describe the damage first — without it the AI cannot tell which damage belongs to this claim';

  // Damage assessment state that drives both the progress strip and which action
  // the toolbar offers.
  const assessmentStatus = displayData.damageAssessmentStatus || 'pending';
  const assessmentRunning = assessmentStatus === 'processing' || generatingAssessment;

  return (
    <QuoteModelProvider
      quoteRequestId={id}
      insurerConfigId={selectedInsurerConfigId}
      vehicleType={vehicleType}
      vehicleSize={vehicleSize}
      labourRate={Number(hourlyRateValue) || 0}
    >
    <div
      className="relative max-w-full h-screen flex flex-col"
      onDragEnter={handlePageDragEnter}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {/* Drop overlay — shown while files are dragged over the page. Its content
          ignores the pointer, so leaving the overlay is the only dragleave. */}
      {fileDragActive && (
        <div
          onDragLeave={(e) => { if (e.target === e.currentTarget) setFileDragActive(false); }}
          className="absolute inset-3 z-40 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-background/85"
        >
          <div className="pointer-events-none flex flex-col items-center gap-2 text-primary">
            <Upload className="h-8 w-8" />
            <p className="text-sm font-medium">Drop photos or PDFs to add them</p>
          </div>
        </div>
      )}

      {/* Header - Frozen at top */}
      <div className="flex items-center justify-between px-6 py-4 border-b flex-wrap gap-3 bg-background z-50 flex-shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          <Button 
            variant="ghost" 
            onClick={handleBackNavigation}
            size="icon"
            className="p-0"
            title="Back to Quote Requests"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Quote Request Details</h1>
          {getStatusBadge(displayData.status)}
          <CollaborationIndicator 
            isOnline={isOnline}
            activeUsers={activeUsers}
            currentUserId={currentUser?.id}
            className="ml-2"
            align="left"
          />
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <QuoteRequestChecklist items={checklistItems} />
          <Tooltip content="Copy link to this quote request">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => copyToClipboard(window.location.href, 'Share link')}
            >
              <LinkIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
          <RecordHistoryButton rowId={id} title="Quote request" />
          <PageHeaderActions searchClassName="w-64" />
        </div>
      </div>

      {/* Main content - Scrollable */}
      <div className="overflow-y-auto flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 p-6">
        <div className="flex flex-col space-y-6">
          {/* Damage Section - Now with Damage Description */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>Damage</CardTitle>
                  {!hasDamageDescription && (
                    <SectionHint label="Describe the damage here" onClick={focusDamageDescription} />
                  )}
                </div>
                <CollapseExpandButton
                  isExpanded={cardStates.damage}
                  onClick={() => toggleCard('damage')}
                />
              </div>
            </CardHeader>
            {cardStates.damage && (
              <CardContent className="space-y-6">
                {/* Damage Description moved here from Work Details */}
                <div className="space-y-2">
                  <Label htmlFor="damage-description">
                    Damage Description
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </Label>
                  <PresenceTextarea
                    id="damage-description"
                    value={displayData.damageDescription || ''}
                    onChange={(e) => handleFieldChange('damageDescription', e.target.value)}
                    onFocus={() => handleFieldFocus('damageDescription')}
                    onBlur={handleFieldBlur}
                    editor={getFieldEditor('damageDescription')}
                    isCurrentUser={isCurrentUserEditing('damageDescription')}
                    placeholder="What happened, and which panels it affected…"
                    className="min-h-[80px]"
                    aria-required="true"
                  />
                </div>

                {/* Toolbar: view mode, filter, add section, and the AI assessment
                    action. ML runs on photos as they upload; the AI assessment +
                    draft quote only run when explicitly asked. */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Button
                      variant={damageFilterOpen ? 'default' : 'outline'}
                      size="sm"
                      onClick={toggleDamageFilter}
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filter
                    </Button>
                    <div className="inline-flex rounded-md border border-input bg-input p-0.5">
                      {DAMAGE_VIEW_MODES.map((mode) => (
                        <button
                          key={mode.id}
                          type="button"
                          onClick={() => setDamageViewMode(mode.id)}
                          className={`h-8 px-3 rounded text-xs font-medium transition-colors ${
                            damageViewMode === mode.id
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {damageViewMode === 'sections' && (
                      <Popover open={addSectionOpen} onOpenChange={setAddSectionOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Section
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-auto p-3">
                          <div className="text-xs font-medium text-muted-foreground mb-2">Add a section</div>
                          <VehicleSectionPicker value={null} onSelect={addSection} />
                        </PopoverContent>
                      </Popover>
                    )}
                    {(() => {
                      // While a run is in flight the progress strip below carries
                      // the state — the button just shows the work is happening
                      // and never blocks editing.
                      if (assessmentRunning) {
                        return (
                          <Button size="sm" disabled>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {hasDamageParts ? 'Updating…' : 'Assessing…'}
                          </Button>
                        );
                      }
                      // There is an assessment to protect: the AI adds what it
                      // finds on top instead of wiping the assessor's work.
                      if (hasDamageParts) {
                        return (
                          <Tooltip content={canAssess
                            ? 'Re-run the AI over the photos and add anything missing. Your lines, hours and prices are kept.'
                            : assessBlockedReason}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateAssessment({ merge: true })}
                              disabled={!canAssess}
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Update with AI
                            </Button>
                          </Tooltip>
                        );
                      }
                      // The orchestrator refused the run because the shop is out
                      // of AI assessments. It fires asynchronously, so this
                      // status is how the refusal reaches the screen at all —
                      // the pre-check above catches the common case, this
                      // catches a stale allowance (second tab, another user).
                      if (assessmentStatus === 'limit_reached') {
                        return (
                          <Tooltip content="Your plan's AI assessments are used up for this month. Everything else still works.">
                            <Button size="sm" variant="outline" onClick={() => navigate('/settings/general')}>
                              <Sparkles className="h-4 w-4 mr-2" />
                              See plans
                            </Button>
                          </Tooltip>
                        );
                      }
                      if (assessmentStatus === 'failed') {
                        return (
                          <Tooltip content={canAssess
                            ? 'The AI assessment did not finish — run it again'
                            : assessBlockedReason}>
                            <Button size="sm" onClick={() => handleGenerateAssessment()} disabled={!canAssess}>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Retry assessment
                            </Button>
                          </Tooltip>
                        );
                      }
                      return (
                        <Tooltip content={canAssess
                          ? 'Run the AI damage assessment and draft a quote'
                          : assessBlockedReason}>
                          <Button size="sm" onClick={() => handleGenerateAssessment()} disabled={!canAssess}>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate damage assessment
                          </Button>
                        </Tooltip>
                      );
                    })()}
                  </div>
                </div>

                {/* Assessment progress. Sits above the list rather than over it:
                    the run takes minutes and the assessor keeps working through
                    it — nothing below is disabled while it runs. */}
                {assessmentRunning && (
                  <AssessmentProgress
                    progress={displayData.damageAssessmentProgress}
                    stage={displayData.damageAssessmentStage}
                    heartbeat={displayData.damageAssessmentHeartbeat}
                    startedAt={displayData.damageAssessmentStartedAt}
                    onStopWaiting={handleStopWaitingForAssessment}
                  />
                )}

                {/* Filter pane (toggled by the Filter button, in any view) + parts
                    list. The zone selector filters every view. */}
                <div className="flex gap-6 items-start">
                  {damageFilterOpen && (
                    <div className="w-36 flex-shrink-0 sticky top-4 self-start">
                      <DamageHotspotSelector
                        selectedZones={damageZones}
                        onChange={setDamageZones}
                        onRemoveZone={removeZone}
                        checkZoneHasParts={checkZoneHasPartsRef}
                        filterZone={damageFilterZone}
                        onFilterChange={setDamageFilterZone}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <DamagePartsList
                      viewMode={damageViewMode}
                      selectedZones={damageZones}
                      onRemoveZone={removeZone}
                      onCheckZoneHasParts={setCheckZoneHasPartsRef}
                      onZonesWithPartsChanged={handleZonesWithPartsChanged}
                      availableImages={quoteRequestImages}
                      onImageClick={handleImageClick}
                      filterZone={damageFilterZone}
                      onAddSection={() => setAddSectionOpen(true)}
                      onGenerateAssessment={() => handleGenerateAssessment()}
                      canGenerateAssessment={canAssess}
                      assessBlockedReason={assessBlockedReason}
                      assessmentRunning={assessmentRunning}
                      trackFieldEdit={trackFieldEdit}
                      clearFieldEdit={clearFieldEdit}
                      getFieldEditor={getFieldEditor}
                      isCurrentUserEditing={isCurrentUserEditing}
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Quote — a single quote per request, a live view over the shared
              damage/quote model (edits here reflect in the damage assessment). */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Quote</CardTitle>
                <CollapseExpandButton
                  isExpanded={cardStates.quotes}
                  onClick={() => toggleCard('quotes')}
                />
              </div>
            </CardHeader>
            {cardStates.quotes && (
              <CardContent>
                <QuoteEditor canUpdateLinePrice={canUpdateLinePrice} />
              </CardContent>
            )}
          </Card>

          {/* Images Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>Photos</CardTitle>
                  {!hasDamagePhotos && (
                    <SectionHint label="Add photos here" onClick={() => setUploadOpen(true)} />
                  )}
                </div>
                <CollapseExpandButton
                  isExpanded={cardStates.images}
                  onClick={() => toggleCard('images')}
                />
              </div>
            </CardHeader>
            {cardStates.images && (
              <CardContent>
                <ImageManager
                  images={quoteRequestImages}
                  onImageRemoved={(imageId) => setQuoteRequestImages(prev => prev.filter(img => img.id !== imageId))}
                  onImageUpdated={(imageId, data) => setQuoteRequestImages(prev => prev.map(img => (
                    img.id === imageId ? { ...img, ...data } : img
                  )))}
                  onImagesReordered={(ordered) => setQuoteRequestImages(ordered)}
                  onImageClick={handleImageClick}
                  onRequestUpload={() => setUploadOpen(true)}
                />
              </CardContent>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col space-y-6">
          {/* Panel Quote Section - only shows for PQ-linked jobs */}
          {displayData.panelQuoteJobId && (() => {
            const lastSync = panelQuoteLastSyncMs;
            const diffMs = lastSync ? panelQuoteNow - lastSync : Infinity;
            const diffMins = diffMs / 60000;
            const isCoolingDown = diffMs < PANEL_QUOTE_SYNC_COOLDOWN_MS;
            const secondsRemaining = Math.max(0, Math.ceil((PANEL_QUOTE_SYNC_COOLDOWN_MS - diffMs) / 1000));

            let lastSyncLabel = 'Never';
            if (lastSync) {
              if (diffMins < 1) lastSyncLabel = 'Just now';
              else if (diffMins < 60) lastSyncLabel = `${Math.floor(diffMins)}m ago`;
              else if (diffMins < 60 * 24) lastSyncLabel = `${Math.floor(diffMins / 60)}h ago`;
              else if (diffMins < 60 * 24 * 7) lastSyncLabel = `${Math.floor(diffMins / (60 * 24))} day${Math.floor(diffMins / (60 * 24)) === 1 ? '' : 's'} ago`;
              else lastSyncLabel = 'Over 1 week ago';
            }

            return (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Panel Quote</CardTitle>
                    <div className="flex items-center space-x-2">
                      <a
                        href={`https://s1.panel-quote.co.nz/member/php/manage-job.php?jobID=${displayData.panelQuoteJobId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        title="Open in Panel Quote"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <CollapseExpandButton
                        isExpanded={cardStates.panelQuote}
                        onClick={() => toggleCard('panelQuote')}
                      />
                    </div>
                  </div>
                </CardHeader>
                {cardStates.panelQuote && (
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Job Number</p>
                      <p className="text-sm">#{priv.jobNumber(displayData.panelQuoteJobNumber || displayData.panelQuoteJobId)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Last Sync</p>
                      <p className="text-sm">{lastSyncLabel}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleUpdatePanelQuote}
                      disabled={updatingPanelQuote || isCoolingDown}
                      title={isCoolingDown ? `Panel Quote allows one sync every 2 minutes — ${secondsRemaining}s to go` : undefined}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {updatingPanelQuote
                        ? 'Updating…'
                        : 'Update Quote'}
                    </Button>
                  </CardContent>
                )}
              </Card>
            );
          })()}

          {/* Panel Quote handoff — shown until the request has a job to link to */}
          {!displayData.panelQuoteJobId && (() => {
            const insurerUnmapped = displayData.isInsuranceWork && panelQuoteInsurerMapping === null;
            const insurerLabel = insurerMap.get(displayData.insuranceCompany)
              || displayData.otherInsurer
              || displayData.insuranceCompany
              || 'This insurer';

            return (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Panel Quote</CardTitle>
                    <CollapseExpandButton
                      isExpanded={cardStates.panelQuote}
                      onClick={() => toggleCard('panelQuote')}
                    />
                  </div>
                </CardHeader>
                {cardStates.panelQuote && (
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Not saved to Panel Quote
                    </p>

                    {insurerUnmapped && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          {insurerLabel} is not mapped to a Panel Quote customer. Map it in your organisation's {' '}
                          <Link to="/settings/integrations" className="underline font-medium">
                            integration settings
                          </Link>{' '}
                          to enable sync
                        </span>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleOpenPanelQuoteCreate}
                      disabled={creatingPanelQuote || insurerUnmapped}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {creatingPanelQuote ? 'Creating…' : 'Create in Panel Quote'}
                    </Button>
                  </CardContent>
                )}
              </Card>
            );
          })()}

          {/* Insurer Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Insurer</CardTitle>
                <CollapseExpandButton
                  isExpanded={cardStates.insurer}
                  onClick={() => toggleCard('insurer')}
                />
              </div>
            </CardHeader>
            {cardStates.insurer && (
              <CardContent className="space-y-4">
                {/* Insurance Work Checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="insurance-work"
                    checked={displayData.isInsuranceWork}
                    onCheckedChange={(checked) => handleFieldChange('isInsuranceWork', checked)}
                  />
                  <Label htmlFor="insurance-work">Insurance Work</Label>
                </div>

                {displayData.isInsuranceWork && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="insurance-company">Insurance Company</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <PresenceSelect
                            placeholder="Select insurer"
                            value={selectedInsurerValue}
                            onValueChange={handleInsurerChange}
                            onOpenChange={(open) => open ? handleFieldFocus('insuranceCompany') : handleFieldBlur()}
                            editor={getFieldEditor('insuranceCompany')}
                            isCurrentUser={isCurrentUserEditing('insuranceCompany')}
                          >
                            <SelectContent>
                              <SelectItem value="other">Other</SelectItem>
                              {insurers.map(insurer => (
                                <SelectItem key={insurer.id} value={insurer.id}>
                                  {insurer.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </PresenceSelect>
                        </div>
                        {hasInsurerGuidelines && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleOpenInsurerGuidelines}
                            title="View insurer guidelines"
                          >
                            <BookOpenText className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {showOtherInsurer && (
                      <div className="space-y-2">
                        <Label htmlFor="other-insurer">Other Insurer</Label>
                        <PresenceInput
                          id="other-insurer"
                          value={displayData.otherInsurer || ''}
                          onChange={(e) => handleFieldChange('otherInsurer', e.target.value)}
                          onFocus={() => handleFieldFocus('otherInsurer')}
                          onBlur={handleFieldBlur}
                          editor={getFieldEditor('otherInsurer')}
                          isCurrentUser={isCurrentUserEditing('otherInsurer')}
                          placeholder="Specify insurer"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="claim-number">Claim Number</Label>
                      <PresenceInput
                        id="claim-number"
                        value={displayData.claimNumber || ''}
                        onChange={(e) => handleFieldChange('claimNumber', e.target.value)}
                        onFocus={() => handleFieldFocus('claimNumber')}
                        onBlur={handleFieldBlur}
                        editor={getFieldEditor('claimNumber')}
                        isCurrentUser={isCurrentUserEditing('claimNumber')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hourly-rate">Hourly Rate</Label>
                      <PresenceInput
                        id="hourly-rate"
                        value={hourlyRateValue === null ? '' : hourlyRateValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numeric = value === '' ? null : parseFloat(value);
                          updateField('hourlyRate', Number.isFinite(numeric) ? numeric : null);
                        }}
                        onFocus={() => handleFieldFocus('hourlyRate')}
                        onBlur={handleFieldBlur}
                        editor={getFieldEditor('hourlyRate')}
                        isCurrentUser={isCurrentUserEditing('hourlyRate')}
                        placeholder={suggestedHourlyRate ? `Suggested $${suggestedHourlyRate}` : '$0.00'}
                        type="number"
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="excess">Excess</Label>
                      {/* ?? rather than ||: a stored excess of 0 is a real answer, not an empty field */}
                      <PresenceInput
                        id="excess"
                        value={displayData.excess ?? ''}
                        onChange={(e) => handleFieldChange('excess', e.target.value)}
                        onFocus={() => handleFieldFocus('excess')}
                        onBlur={handleFieldBlur}
                        editor={getFieldEditor('excess')}
                        isCurrentUser={isCurrentUserEditing('excess')}
                        placeholder="$0.00"
                        type="number"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            )}
          </Card>

          {/* Customer Info Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>Customer</CardTitle>
                  {!hasCustomer && (
                    <SectionHint label="Link a customer here" onClick={() => setLinkCustomerOpen(true)} />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {displayData.customerId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenCustomerForm(displayData.customerId)}
                    >
                      Edit
                    </Button>
                  )}
                  {currentOrgId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedBusinessId(currentOrgId); setCustomerOrganisationFormOpen(true); }}
                    >
                      Edit
                    </Button>
                  )}
                  <CollapseExpandButton
                    isExpanded={cardStates.customer}
                    onClick={() => toggleCard('customer')}
                  />
                </div>
              </div>
            </CardHeader>
            {cardStates.customer && (
              currentOrgId ? (
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Business</p>
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {linkedOrganisation?.name || '…'}
                    </p>
                  </div>
                  {linkedOrganisation?.tradingName && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Trading as</p>
                      <p className="text-sm">{linkedOrganisation.tradingName}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="text-sm">{priv.phone(firstOfJsonArray(linkedOrganisation?.phones)) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm">{priv.email(firstOfJsonArray(linkedOrganisation?.emails), linkedOrganisation?.name) || '—'}</p>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="loan-vehicle"
                        checked={displayData.needsLoanVehicle}
                        onCheckedChange={(checked) => handleFieldChange('needsLoanVehicle', checked)}
                      />
                      <Label htmlFor="loan-vehicle" className="text-sm font-medium text-muted-foreground">
                        Loan Vehicle Required
                      </Label>
                    </div>
                  </div>
                </CardContent>
              ) : displayData.customer ? (
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Name</p>
                    <p className="text-sm">{priv.name(`${displayData.customer.firstName || ''} ${displayData.customer.lastName || ''}`.trim()) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="text-sm">{priv.phone(displayData.customer.phone) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm">{priv.email(displayData.customer.email, displayData.customer.firstName) || '—'}</p>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="loan-vehicle"
                        checked={displayData.needsLoanVehicle}
                        onCheckedChange={(checked) => handleFieldChange('needsLoanVehicle', checked)}
                      />
                      <Label htmlFor="loan-vehicle" className="text-sm font-medium text-muted-foreground">
                        Loan Vehicle Required
                      </Label>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <CardContent>
                  <Button size="sm" variant="outline" onClick={() => setLinkCustomerOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Link customer
                  </Button>
                </CardContent>
              )
            )}
          </Card>

          {/* Vehicle Info Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>Vehicle</CardTitle>
                  {!hasVehicle && (
                    <SectionHint label="Add the vehicle here" onClick={() => setLinkVehicleOpen(true)} />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {displayData.customerVehicleId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenVehicleForm(displayData.customerVehicleId)}
                    >
                      Edit
                    </Button>
                  )}
                  <CollapseExpandButton
                    isExpanded={cardStates.vehicle}
                    onClick={() => toggleCard('vehicle')}
                  />
                </div>
              </div>
            </CardHeader>
            {cardStates.vehicle && (
              displayData.vehicle ? (
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Registration</p>
                    <p className="text-sm font-mono">{priv.rego(displayData.vehicle.registration) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Make & Model</p>
                    <p className="text-sm">
                      {(() => {
                        const make = displayData.vehicle.make === 'Other'
                          ? (displayData.vehicle.otherMake || 'Other')
                          : displayData.vehicle.make;
                        const model = displayData.vehicle.model === 'Other'
                          ? (displayData.vehicle.otherModel || '')
                          : displayData.vehicle.model;
                        return `${make || ''} ${model || ''}`.trim() || '—';
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Year</p>
                    <p className="text-sm">{displayData.vehicle.year || '—'}</p>
                  </div>

                  {/* Business-owned vehicle: show owner + a driver (contact) */}
                  {currentOrgId && (
                    <div className="pt-2 border-t space-y-3">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Owner</p>
                        <p className="text-sm flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {linkedOrganisation?.name || '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Driver</p>
                        {(() => {
                          const driver = displayData.vehicle.driver;
                          const driverObj = driver && typeof driver === 'object' ? driver : null;
                          if (driverObj) {
                            return (
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                                  {priv.name(`${driverObj.firstName || ''} ${driverObj.lastName || ''}`.trim()) || '—'}
                                </p>
                                <Button variant="ghost" size="sm" onClick={() => setLinkDriverOpen(true)}>
                                  Change
                                </Button>
                              </div>
                            );
                          }
                          return (
                            <Button size="sm" variant="outline" className="mt-1" onClick={() => setLinkDriverOpen(true)}>
                              <User className="h-4 w-4 mr-2" />
                              Link driver
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </CardContent>
              ) : (
                <CardContent>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLinkVehicleOpen(true)}
                  >
                    <CarFront className="h-4 w-4 mr-2" />
                    Link vehicle
                  </Button>
                </CardContent>
              )
            )}
          </Card>

          {/* Timeline Section */}
          <Card
            onMouseEnter={() => setIsHoveringTimeline(true)}
            onMouseLeave={() => setIsHoveringTimeline(false)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Timeline</CardTitle>
                <CollapseExpandButton
                  isExpanded={cardStates.timeline}
                  onClick={() => toggleCard('timeline')}
                />
              </div>
            </CardHeader>
            {cardStates.timeline && (
              <CardContent>
                <Timeline
                  key={timelineKey}
                  recordType="quote_requests"
                  recordId={id}
                  onNoteClick={handleNoteClick}
                  onNewNote={handleNewNote}
                  isHovering={isHoveringTimeline}
                />
              </CardContent>
            )}
          </Card>
        </div>
        </div>
      </div>

      {/* Link customer / vehicle / driver dialogs */}
      {linkCustomerOpen && (
        <LinkCustomerDialog
          isOpen={linkCustomerOpen}
          onClose={() => setLinkCustomerOpen(false)}
          onLinked={handleCustomerLinked}
        />
      )}
      {linkVehicleOpen && (
        <LinkVehicleDialog
          isOpen={linkVehicleOpen}
          onClose={() => setLinkVehicleOpen(false)}
          customerId={displayData.customerId || null}
          ownerOrganisationId={currentOrgId}
          onLinked={handleVehicleLinked}
        />
      )}
      {linkDriverOpen && (
        <LinkCustomerDialog
          isOpen={linkDriverOpen}
          onClose={() => setLinkDriverOpen(false)}
          onLinked={handleDriverLinked}
          individualOnly
          title="Link Driver"
        />
      )}
      {uploadOpen && (
        <ImageUploadDialog
          isOpen={uploadOpen}
          onClose={closeUploadDialog}
          initialFiles={droppedFiles}
          quoteRequestId={id}
          startOrder={quoteRequestImages.length}
          onUploaded={(record) => setQuoteRequestImages(prev => (
            prev.some(img => img.id === (record.$id || record.id))
              ? prev
              : sortImagesByOrder([...prev, mapImageRecord(record)])
          ))}
        />
      )}

      {/* Overlay Forms */}
      {customerFormOpen && selectedCustomerId && (
        <CustomerForm
          customerId={selectedCustomerId}
          isOpen={customerFormOpen}
          onClose={handleCloseCustomerForm}
        />
      )}

      {vehicleFormOpen && selectedVehicleId && (
        <VehicleForm
          vehicleId={selectedVehicleId}
          isOpen={vehicleFormOpen}
          onClose={handleCloseVehicleForm}
        />
      )}

      {organizationFormOpen && (
        <OrganizationForm
          onClose={handleCloseOrganizationForm}
          onSave={handleOrganizationFormSave}
        />
      )}

      {customerOrganisationFormOpen && (
        <CustomerOrganisationForm
          organisationId={selectedBusinessId}
          isOpen={customerOrganisationFormOpen}
          initialName={organisationFormInitialName}
          onClose={handleCloseCustomerOrganisationForm}
          onSave={handleCustomerOrganisationFormSave}
        />
      )}

      {/* Damage Image Form with bounding boxes */}
      <DamageImageForm
        images={images}
        initialIndex={selectedImageIndex}
        isOpen={imageViewerOpen}
        onClose={handleCloseImageViewer}
        onImageUpdate={handleImageUpdate}
        quoteRequestId={id}
        insurerConfigId={selectedInsurerConfigId}
      />

      {/* Note Form */}
      <NoteForm
        note={selectedNote}
        recordType="quote_requests"
        recordId={id}
        isOpen={noteFormOpen}
        onClose={handleNoteClose}
        onSave={handleNoteSave}
        onDelete={handleNoteDelete}
      />

      {/* Panel Quote Update Confirmation Dialog */}
      <Dialog open={showPanelQuoteConfirm} onOpenChange={setShowPanelQuoteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Quote to Panel Quote</DialogTitle>
            <DialogDescription>
              This replaces the line items on Panel Quote job
              #{priv.jobNumber(displayData.panelQuoteJobNumber || displayData.panelQuoteJobId)} with this quote.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Anything entered against the job in Panel Quote will be overwritten. Any current sessions on the job will be overridden.
            </span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPanelQuoteConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmPanelQuoteUpdate}>
              Send to Panel Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Panel Quote Create Job Dialog */}
      <Dialog open={showPanelQuoteCreate} onOpenChange={setShowPanelQuoteCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create in Panel Quote</DialogTitle>
            <DialogDescription>
              A new Panel Quote job will be created for this request and saved with its quote's line
              items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Vehicle</span>
                <span className="text-right">
                  {[displayData.vehicle?.make, displayData.vehicle?.model].filter(Boolean).join(' ') || 'Not set'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Billed to</span>
                <span className="text-right">
                  {displayData.isInsuranceWork
                    ? (panelQuoteInsurerMapping?.panelQuoteCustomerName || 'Insurer')
                    : (linkedOrganisation?.name
                      || priv.name([displayData.customer?.firstName, displayData.customer?.lastName].filter(Boolean).join(' '))
                      || 'Left blank')}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Photos</span>
                <span className="text-right">
                  {images.length > 0 ? `${images.length} to upload` : 'None'}
                </span>
              </div>
              {displayData.isInsuranceWork && (
                <p className="text-xs text-muted-foreground pt-1">
                  The vehicle owner goes across as the excess customer, which is how Panel Quote holds
                  them on an insurance job.
                </p>
              )}
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPanelQuoteCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePanelQuoteJob}>
              Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </QuoteModelProvider>
  );
};

export default QuoteRequestDetailsRealtime;
